"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { useReducedMotion } from "@/lib/motion";
import { Button } from "@/components/ui/";
import { ChevronLeft, ChevronRight, ArrowRight, Pause, Play } from "@/components/icons";
import { BLOG_POSTS } from "@/content/blog";
import { BlogPostCard } from "./blog-post-card";
import type { HomeBlogPost } from "./fetch";

const FALLBACK_POSTS: HomeBlogPost[] = BLOG_POSTS.slice(0, 10).map((post, index) => ({
  id: index + 1,
  title: post.title,
  category: post.category,
  excerpt: post.excerpt,
  author: post.author,
  authorInitial: post.authorInitial,
  authorImage: null,
  image: null,
  date: post.date,
  readTime: post.readTime,
  href: `/blog/${post.slug}`,
}));

/** Drift speed of the marquee, in CSS pixels per second. */
const MARQUEE_SPEED = 40;
/** How long the drift stays paused after a manual scroll, drag or arrow click. */
const INTERACTION_PAUSE_MS = 2000;

/* Cards used to overlap by a pixel (`-ml-px`) so adjacent borders collapsed
   into one hairline; they're separate rounded cards now, so they sit apart on
   the list's own gap instead. */
const CARD_WIDTH = "relative w-[300px] shrink-0 sm:w-[340px]";

type Props = { posts?: HomeBlogPost[] };

export default function BlogSection({ posts }: Props = {}) {
  const items = posts && posts.length > 0 ? posts : FALLBACK_POSTS;
  const sliderRef = useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const prefersReducedMotion = useReducedMotion();

  // The row only drifts when motion is welcome. Under reduced motion it stays a
  // plain scroller with a single set of cards, exactly as before.
  const marquee = !prefersReducedMotion;

  // Refs (not state) so the animation frame never triggers a re-render.
  const holdRef = useRef(false); // pointer is over the row, or focus is inside it
  const resumeAtRef = useRef(0); // timestamp to resume at after an interaction
  const periodRef = useRef(0); // px covered by one full set of cards + its gap
  const offsetRef = useRef(0); // fractional scroll position we drive

  const holdDrift = useCallback(() => {
    holdRef.current = true;
  }, []);

  const releaseDrift = useCallback(() => {
    holdRef.current = false;
  }, []);

  const deferDrift = useCallback(() => {
    resumeAtRef.current = performance.now() + INTERACTION_PAUSE_MS;
  }, []);

  // Arrow enablement only matters for the static scroller — the marquee loops,
  // so both directions are always available. Skipping the listener in marquee
  // mode also keeps the per-frame scroll writes from re-rendering the section.
  useEffect(() => {
    if (marquee) return;
    const el = sliderRef.current;
    if (!el) return;
    const check = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 8);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 8);
    };
    el.addEventListener("scroll", check, { passive: true });
    check();
    return () => el.removeEventListener("scroll", check);
  }, [marquee]);

  // Measure the loop period from the first clone's offset rather than halving
  // scrollWidth — the doubled list has one extra gap, so scrollWidth / 2 would
  // drift by half a gap on every lap. Measured once, then on resize only.
  useEffect(() => {
    if (!marquee) return;
    const el = sliderRef.current;
    if (!el) return;
    const measure = () => {
      const first = el.children[0] as HTMLElement | undefined;
      const firstClone = el.children[items.length] as HTMLElement | undefined;
      periodRef.current = first && firstClone ? firstClone.offsetLeft - first.offsetLeft : 0;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [marquee, items.length]);

  useEffect(() => {
    if (!marquee || !isPlaying) return;
    const el = sliderRef.current;
    if (!el) return;

    let frame = 0;
    let last = 0;
    offsetRef.current = el.scrollLeft;

    const step = (now: number) => {
      if (!last) last = now;
      // Clamp so a backgrounded tab doesn't resume with one giant jump.
      const elapsed = Math.min((now - last) / 1000, 0.05);
      last = now;

      const period = periodRef.current;
      const running = !holdRef.current && now >= resumeAtRef.current && !document.hidden;

      if (running && period > 0) {
        let next = offsetRef.current + MARQUEE_SPEED * elapsed;
        // The second set is identical, so wrapping back by one period is seamless.
        if (next >= period) next -= period;
        offsetRef.current = next;
        el.scrollLeft = next;
      } else {
        // Paused: follow whatever the visitor scrolled to.
        offsetRef.current = el.scrollLeft;
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [marquee, isPlaying]);

  const scroll = (dir: "left" | "right") => {
    if (!sliderRef.current) return;
    deferDrift();
    sliderRef.current.scrollBy({
      left: dir === "left" ? -360 : 360,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  return (
    <section
      id="blog"
      /* full-bleed so the band spans the viewport rather than stopping at the
         frame hairlines — same treatment as the other tinted bands. */
      className="full-bleed py-section border-border bg-band border-b"
      aria-labelledby="blog-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Blog & Resources</p>
            <h2
              id="blog-heading"
              className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Insights worth reading.
            </h2>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <Link
              href="/blog"
              className="text-muted-foreground flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              View all articles
              <ArrowRight size={14} />
            </Link>
            <div className="flex gap-2">
              {marquee && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsPlaying((playing) => !playing)}
                  aria-label={isPlaying ? "Pause blog post carousel" : "Play blog post carousel"}
                  aria-pressed={!isPlaying}
                  className="h-9 w-9"
                >
                  {isPlaying ? <Pause size={15} /> : <Play size={15} />}
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => scroll("left")}
                disabled={!marquee && !canScrollLeft}
                aria-label="Scroll blog posts left"
                className="h-9 w-9"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scroll("right")}
                disabled={!marquee && !canScrollRight}
                aria-label="Scroll blog posts right"
                className="h-9 w-9"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>

        <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
          <div
            className="3xl:w-32 from-background pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r to-transparent"
            aria-hidden="true"
          />
          <div
            className="3xl:w-32 from-background pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l to-transparent"
            aria-hidden="true"
          />

          <ul
            ref={sliderRef}
            className="scrollbar-hide flex list-none gap-4 overflow-x-auto px-4 pb-4 sm:gap-5 sm:px-6 lg:px-8"
            aria-label="Blog posts"
            onMouseEnter={marquee ? holdDrift : undefined}
            onMouseLeave={marquee ? releaseDrift : undefined}
            onFocus={marquee ? holdDrift : undefined}
            onBlur={marquee ? releaseDrift : undefined}
            onPointerDown={marquee ? deferDrift : undefined}
            onTouchStart={marquee ? deferDrift : undefined}
            onWheel={marquee ? deferDrift : undefined}
          >
            {items.map((post, i) => (
              <li key={post.id} className={CARD_WIDTH}>
                <BlogPostCard post={post} index={i} prefersReducedMotion={prefersReducedMotion} />
              </li>
            ))}
            {marquee &&
              items.map((post, i) => (
                <li key={`dup-${post.id}`} className={CARD_WIDTH} aria-hidden="true">
                  <BlogPostCard
                    post={post}
                    index={i}
                    prefersReducedMotion={prefersReducedMotion}
                    duplicate
                  />
                </li>
              ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
